// @givmo/board — "Add a rule" on a committee's "Its rules" card (CBG-YR6-AUTH,
// `board-committees.md` §8). The Meeting rules dialog's shape and its approved words, scoped to one
// committee: its notice period in days, its quorum as a number or a fraction of its members, and how
// its minutes are approved (the board's three ways, said of the committee).
//
// The citation is required, as for every recorded rule: there is no path by which a number arrives
// unattributed. Recording a rule supersedes the one in force and keeps it in the committee's history;
// a concurrent recording of the same rule is the backend's 409 in its own words, and the person's
// input stays. Line 8b's answer is recorded through its own dialog (`authority-dialog.tsx`), never
// here. Recording is `board_governance_parameter:manage`.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, type BoardCommitteeOut } from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { committeesWords as w } from './committees-words';

type RuleKind = 'notice_period' | 'quorum' | 'minutes_approval';
type QuorumBasis = 'seat_count' | 'fraction';
const WAYS = ['written_consent', 'next_meeting', 'officer'] as const;

/** Whether a field holds a plain whole number (the only shape these figures take). */
function isWholeNumber(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

function toWholeNumber(value: string): number {
  return Number.parseInt(value.trim(), 10);
}

export function CommitteeRuleDialog({
  orgRef,
  committee,
  onClose,
  onDone,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const [kind, setKind] = useState<RuleKind>('notice_period');
  const [basis, setBasis] = useState<QuorumBasis>('seat_count');
  const [days, setDays] = useState('');
  const [count, setCount] = useState('');
  const [numerator, setNumerator] = useState('');
  const [denominator, setDenominator] = useState('');
  const [way, setWay] = useState<string>('');
  const [citation, setCitation] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: async () => {
      // Exactly the value fields the basis uses: the backend's CHECK pins the pairing.
      const shape =
        kind === 'notice_period'
          ? { basis: 'days' as const, days_value: toWholeNumber(days) }
          : kind === 'minutes_approval'
            ? { basis: 'choice' as const, choice_value: way }
            : basis === 'seat_count'
              ? { basis: 'seat_count' as const, count_value: toWholeNumber(count) }
              : {
                  basis: 'fraction' as const,
                  numerator: toWholeNumber(numerator),
                  denominator: toWholeNumber(denominator),
                };
      const { data } = await operations.officerRecordBoardCommitteeRule({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: {
          rule_kind: kind,
          ...shape,
          citation: citation.trim(),
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(w.ruleRecorded);
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.ruleFailed),
  });

  const valueReady =
    kind === 'notice_period'
      ? isWholeNumber(days)
      : kind === 'minutes_approval'
        ? way.length > 0
        : basis === 'seat_count'
          ? isWholeNumber(count)
          : isWholeNumber(numerator) && isWholeNumber(denominator);
  const pending = record.isPending;
  const canSave = valueReady && citation.trim().length > 0 && !pending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label={w.ruleDialogTitle}
        className="flex flex-col gap-16 overflow-y-auto"
        testId="committee-rule-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {w.ruleDialogTitle}
        </Dialog.Title>
        <span className="-mt-8 font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          {committee.name}
        </span>

        <Select
          label={w.whichRule}
          appearance="saas"
          value={kind}
          disabled={pending}
          onValueChange={(next) => next !== null && setKind(next as RuleKind)}
          options={[
            { value: 'notice_period', label: w.noticeLabel },
            { value: 'quorum', label: w.quorumLabel },
            { value: 'minutes_approval', label: w.minutesWayLabel },
          ]}
          testId="committee-rule-kind"
        />

        {kind === 'notice_period' ? (
          <>
            <TextField
              label={w.daysOfNotice}
              inputMode="numeric"
              value={days}
              disabled={pending}
              onChange={(event) => setDays(event.target.value)}
              testId="committee-rule-days"
            />
            <FieldNote>{w.daysNote}</FieldNote>
          </>
        ) : kind === 'minutes_approval' ? (
          <RadioTileGroup
            value={way}
            onValueChange={setWay}
            aria-label={w.minutesWayLabel}
            className="flex flex-col gap-12"
            testId="committee-rule-way"
          >
            {WAYS.map((choice) => (
              <RadioTile
                key={choice}
                value={choice}
                label={w.wayLabel(choice)}
                description={w.wayDescription(choice)}
                testId={`committee-rule-way-${choice}`}
              />
            ))}
          </RadioTileGroup>
        ) : (
          <>
            <Select
              label={w.statedAs}
              appearance="saas"
              value={basis}
              disabled={pending}
              onValueChange={(next) => setBasis(next === 'fraction' ? 'fraction' : 'seat_count')}
              options={[
                { value: 'seat_count', label: w.aNumberOfMembers },
                { value: 'fraction', label: w.aFractionOfMembers },
              ]}
              testId="committee-rule-basis"
            />
            {basis === 'seat_count' ? (
              <TextField
                label={w.numberOfMembers}
                inputMode="numeric"
                value={count}
                disabled={pending}
                onChange={(event) => setCount(event.target.value)}
                testId="committee-rule-count"
              />
            ) : (
              <div className="grid grid-cols-2 gap-12">
                <TextField
                  label={w.thisMany}
                  inputMode="numeric"
                  value={numerator}
                  disabled={pending}
                  onChange={(event) => setNumerator(event.target.value)}
                  testId="committee-rule-numerator"
                />
                <TextField
                  label={w.outOf}
                  inputMode="numeric"
                  value={denominator}
                  disabled={pending}
                  onChange={(event) => setDenominator(event.target.value)}
                  testId="committee-rule-denominator"
                />
              </div>
            )}
          </>
        )}

        <div className="flex flex-col gap-6">
          <TextField
            label={w.citationLabel}
            value={citation}
            maxLength={200}
            disabled={pending}
            onChange={(event) => setCitation(event.target.value)}
            testId="committee-rule-citation"
          />
          <FieldNote>{w.citationNote}</FieldNote>
        </div>

        <Textarea
          label={w.noteLabel}
          value={note}
          rows={2}
          maxLength={2000}
          disabled={pending}
          onChange={(event) => setNote(event.target.value)}
          testId="committee-rule-note"
        />

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          {w.replacesLine}
        </p>

        {error !== null ? <ErrorMessage testId="committee-rule-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={onClose}
            testId="committee-rule-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={pending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="committee-rule-save"
          >
            {w.recordIt}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
